import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useMemo, useState } from 'react';

export type BookableItemOption = {
    id: string;
    nameDe: string;
    nameEn: string;
    type: 'SERVICE' | 'BUNDLE';
};

const typeLabel: Record<'SERVICE' | 'BUNDLE', string> = {
    SERVICE: 'Leistung',
    BUNDLE: 'Bundle',
};

type BookableItemComboboxProps = {
    items: BookableItemOption[];
    value: string | undefined;
    onValueChange: (id: string) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
};

const BookableItemCombobox = ({
    items,
    value,
    onValueChange,
    placeholder = 'Leistung wählen…',
    disabled,
    className,
}: BookableItemComboboxProps) => {
    const [open, setOpen] = useState(false);

    const selected = useMemo(() => items.find((i) => i.id === value), [items, value]);

    const label = selected
        ? `${selected.nameDe} (${typeLabel[selected.type]})`
        : null;

    return (
        <Popover modal={false} open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant='outline'
                    role='combobox'
                    aria-expanded={open}
                    disabled={disabled}
                    className={cn('w-full justify-between font-normal', className)}
                >
                    <span className={cn('truncate', !label && 'text-muted-foreground')}>
                        {label ?? placeholder}
                    </span>
                    <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                </Button>
            </PopoverTrigger>
            <PopoverContent className='w-[var(--radix-popover-trigger-width)] p-0' align='start'>
                <Command
                    key={open ? 'open' : 'closed'}
                    filter={(haystack, needle) => {
                        if (!needle.trim()) return 1;
                        const n = needle.toLowerCase();
                        return haystack.toLowerCase().includes(n) ? 1 : 0;
                    }}
                >
                    <CommandInput placeholder='Leistung suchen…' />
                    <CommandList>
                        <CommandEmpty>Keine Treffer.</CommandEmpty>
                        <CommandGroup>
                            {items.map((item) => {
                                const searchValue = `${item.nameDe} ${item.nameEn} ${typeLabel[item.type]} ${item.id}`;
                                return (
                                    <CommandItem
                                        key={item.id}
                                        value={searchValue}
                                        onSelect={() => {
                                            onValueChange(item.id);
                                            setOpen(false);
                                        }}
                                    >
                                        <Check
                                            className={cn(
                                                'mr-2 h-4 w-4 shrink-0',
                                                value === item.id ? 'opacity-100' : 'opacity-0'
                                            )}
                                        />
                                        <div className='flex min-w-0 flex-col'>
                                            <span className='truncate font-medium'>{item.nameDe}</span>
                                            <span className='truncate text-xs text-muted-foreground'>
                                                {item.nameEn} · {typeLabel[item.type]}
                                            </span>
                                        </div>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

export default BookableItemCombobox;
